/* Enquiries tab — read + status pill + archive + delete + bulk actions.
   Click row to expand. Click pill to update status. Tick to multi-select.
   Use the bulk bar to archive/delete/export many at once. Archived enquiries
   are hidden by default — click the "Archived" filter to view/restore. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Inbox, RefreshCcw, Mail, Phone, Calendar, Download, ChevronDown, Check, Archive, Trash2, ArchiveRestore } from "lucide-react";
import { adminApi, adminDownloadPdf } from "./adminApi";

const STATUS_META = {
 new:     { label: "New",     pill: "bg-amber-400/15 text-amber-200 border-amber-400/40 hover:bg-amber-400/25" },
 quoted:  { label: "Quoted",  pill: "bg-sky-400/15 text-sky-200 border-sky-400/40 hover:bg-sky-400/25" },
 replied: { label: "Replied", pill: "bg-cyan-400/15 text-cyan-200 border-cyan-400/40 hover:bg-cyan-400/25" },
 booked:  { label: "Booked",  pill: "bg-emerald-400/15 text-emerald-200 border-emerald-400/40 hover:bg-emerald-400/25" },
 lost:    { label: "Lost",    pill: "bg-rose-400/15 text-rose-200 border-rose-400/40 hover:bg-rose-400/25" },
};
const STATUS_ORDER = ["new", "quoted", "replied", "booked", "lost"];


/* Click-to-change status pill. */
function StatusPill({ enquiry, onChanged, testidBase }) {
 const status = (enquiry.status || "new").toLowerCase();
 const meta = STATUS_META[status] || STATUS_META.new;
 const [open, setOpen] = useState(false);
 const [savedAt, setSavedAt] = useState(0);
 const wrapRef = useRef(null);

 useEffect(() => {
  if (!open) return;
  const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
  document.addEventListener("mousedown", onDoc);
  return () => document.removeEventListener("mousedown", onDoc);
 }, [open]);

 useEffect(() => {
  if (!savedAt) return;
  const t = setTimeout(() => setSavedAt(0), 2200);
  return () => clearTimeout(t);
 }, [savedAt]);

 const choose = async (next) => {
  setOpen(false);
  if (next === status) return;
  try { await onChanged(next); setSavedAt(Date.now()); } catch { /* parent toast */ }
 };

 return (
  <span ref={wrapRef} className="relative inline-flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
   <button type="button" onClick={() => setOpen((v) => !v)}
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-[0.18em] font-black transition-colors ${meta.pill}`}
    data-testid={testidBase}>
    {meta.label}<ChevronDown className="w-3 h-3 opacity-70" />
   </button>
   {savedAt ? (
    <span className="inline-flex items-center gap-1 text-emerald-300 text-[10px] uppercase tracking-wider font-black" data-testid={`${testidBase}-saved`}>
     <Check className="w-3 h-3" />
    </span>
   ) : null}
   {open && (
    <div className="absolute top-full left-0 mt-1 z-30 min-w-[8.5rem] rounded-xl border border-white/15 bg-[#0F1525] shadow-2xl overflow-hidden" data-testid={`${testidBase}-menu`}>
     {STATUS_ORDER.map((s) => {
      const m = STATUS_META[s];
      const isActive = s === status;
      return (
       <button key={s} type="button" onClick={() => choose(s)}
        className={`w-full text-left px-3 py-2 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors flex items-center justify-between ${isActive ? "bg-white/[0.06] text-white" : "text-white/75 hover:bg-white/[0.06] hover:text-white"}`}
        data-testid={`${testidBase}-opt-${s}`}>
        <span className="inline-flex items-center gap-2">
         <span className={`w-1.5 h-1.5 rounded-full ${m.pill.split(" ")[0]}`} />
         {m.label}
        </span>
        {isActive && <Check className="w-3 h-3 text-emerald-300" />}
       </button>
      );
     })}
    </div>
   )}
  </span>
 );
}


export default function AdminEnquiries({ onUnauthorized, showToast }) {
 const [items, setItems] = useState([]);
 const [loading, setLoading] = useState(true);
 const [open, setOpen] = useState(null); // expanded row id
 const [filter, setFilter] = useState("all"); // all | new | quoted | ... | archived
 const [selected, setSelected] = useState(new Set());
 const [busy, setBusy] = useState(false);

 const load = useCallback(async () => {
  setLoading(true);
  setSelected(new Set());
  try {
   const params = filter === "archived" ? "?limit=500&archived_only=true" : "?limit=500";
   const res = await adminApi(`/admin/enquiries${params}`);
   setItems(res.items || []);
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Failed to load enquiries", "err");
  } finally { setLoading(false); }
 }, [onUnauthorized, showToast, filter]);

 useEffect(() => { load(); }, [load]);

 const counts = useMemo(() => {
  const c = { all: items.length };
  for (const it of items) {
   const s = (it.status || "new").toLowerCase();
   c[s] = (c[s] || 0) + 1;
  }
  return c;
 }, [items]);

 const filtered = useMemo(() => {
  // When viewing archived, filter view shows everything (no extra status filter)
  if (filter === "archived" || filter === "all") return items;
  return items.filter((it) => (it.status || "new").toLowerCase() === filter);
 }, [items, filter]);

 const updateStatus = async (enquiry, next) => {
  const prevItems = items;
  setItems((cur) => cur.map((it) => it.id === enquiry.id ? { ...it, status: next } : it));
  try {
   await adminApi(`/admin/enquiries/${enquiry.id}/status`, { method: "PATCH", body: { status: next } });
  } catch (e) {
   setItems(prevItems);
   if (e.status === 401) { onUnauthorized(); throw e; }
   showToast(e.message || "Status update failed", "err");
   throw e;
  }
 };

 const toggleSelect = (id) => {
  setSelected((cur) => {
   const next = new Set(cur);
   if (next.has(id)) next.delete(id); else next.add(id);
   return next;
  });
 };

 const toggleSelectAll = () => {
  const visibleIds = filtered.map((it) => it.id).filter(Boolean);
  setSelected((cur) => {
   const allSelected = visibleIds.length > 0 && visibleIds.every((id) => cur.has(id));
   if (allSelected) {
    const next = new Set(cur);
    visibleIds.forEach((id) => next.delete(id));
    return next;
   }
   const next = new Set(cur);
   visibleIds.forEach((id) => next.add(id));
   return next;
  });
 };

 const archiveOne = async (enquiry, archived = true) => {
  setBusy(true);
  try {
   await adminApi(`/admin/enquiries/${enquiry.id}/archive`, { method: "PATCH", body: { archived } });
   showToast(archived ? "Enquiry archived." : "Enquiry restored.", "ok");
   load();
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Archive failed", "err");
  } finally { setBusy(false); }
 };

 const deleteOne = async (enquiry) => {
  if (!window.confirm(`Delete enquiry from ${enquiry.name || enquiry.email || "this customer"}? This cannot be undone.`)) return;
  setBusy(true);
  try {
   await adminApi(`/admin/enquiries/${enquiry.id}`, { method: "DELETE" });
   showToast("Enquiry deleted.", "ok");
   load();
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Delete failed", "err");
  } finally { setBusy(false); }
 };

 const bulkArchive = async (archived = true) => {
  const ids = [...selected];
  if (!ids.length) return;
  setBusy(true);
  try {
   const res = await adminApi(`/admin/enquiries/bulk-archive`, { method: "POST", body: { ids, archived } });
   showToast(`${archived ? "Archived" : "Restored"} ${res.modified} enquir${res.modified === 1 ? "y" : "ies"}.`, "ok");
   load();
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Bulk action failed", "err");
  } finally { setBusy(false); }
 };

 const bulkDelete = async () => {
  const ids = [...selected];
  if (!ids.length) return;
  if (!window.confirm(`Delete ${ids.length} selected enquir${ids.length === 1 ? "y" : "ies"}? This cannot be undone.`)) return;
  setBusy(true);
  try {
   const res = await adminApi(`/admin/enquiries/bulk-delete`, { method: "POST", body: { ids } });
   showToast(`Deleted ${res.deleted} enquir${res.deleted === 1 ? "y" : "ies"}.`, "ok");
   load();
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "Bulk delete failed", "err");
  } finally { setBusy(false); }
 };

 const exportSelected = async () => {
  const ids = [...selected];
  if (!ids.length) return;
  try {
   await adminDownloadPdf(`/admin/enquiries/export/pdf?ids=${encodeURIComponent(ids.join(","))}`);
   showToast(`Exported ${ids.length} selected as PDF.`, "ok");
  } catch (e) {
   if (e.status === 401) { onUnauthorized(); return; }
   showToast(e.message || "PDF export failed", "err");
  }
 };

 const visibleIds = filtered.map((it) => it.id).filter(Boolean);
 const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
 const someSelected = selected.size > 0;
 const viewingArchived = filter === "archived";

 return (
  <div data-testid="admin-tab-enquiries">
   <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
    <div>
     <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-[0.32em] font-black mb-2">
      <Inbox className="w-3.5 h-3.5" /> Enquiries &middot; {items.length}{viewingArchived ? " (archived)" : ""}
     </div>
     <p className="text-white/65 text-sm max-w-2xl">Every lead captured from the public enquiry forms. Click the status pill to triage. Tick the checkbox to bulk-archive or delete. Archived items are hidden by default.</p>
    </div>
    <div className="flex items-center gap-2 flex-wrap">
     <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/15 hover:bg-white/[0.06] text-white/75 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors" data-testid="admin-enquiries-refresh"><RefreshCcw className="w-3 h-3" /> Refresh</button>
     <button
      onClick={async () => {
       try {
        const params = viewingArchived ? "?archived_only=true" : "";
        await adminDownloadPdf(`/admin/enquiries/export/pdf${params}`);
        showToast(`Exported ${items.length} enquiries as PDF.`);
       } catch (e) {
        if (e.status === 401) { onUnauthorized(); return; }
        showToast(e.message || "PDF export failed", "err");
       }
      }}
      disabled={!items.length}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-[#0A0A0A] text-[10px] uppercase tracking-[0.22em] font-black disabled:opacity-30 disabled:cursor-not-allowed hover:scale-[1.02] transition-transform"
      data-testid="admin-enquiries-pdf">
      <Download className="w-3 h-3" /> Export PDF
     </button>
    </div>
   </div>

   {/* Filter pills */}
   <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-enquiries-filters">
    <button onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em] font-black transition-colors ${filter === "all" ? "bg-amber-400 text-[#0A0A0A]" : "border border-white/15 text-white/70 hover:bg-white/[0.06]"}`} data-testid="admin-enquiries-filter-all">All ({counts.all || 0})</button>
    {STATUS_ORDER.map((s) => (
     <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em] font-black transition-colors ${filter === s ? "bg-amber-400 text-[#0A0A0A]" : "border border-white/15 text-white/70 hover:bg-white/[0.06]"}`} data-testid={`admin-enquiries-filter-${s}`}>
      {STATUS_META[s].label} ({counts[s] || 0})
     </button>
    ))}
    <button onClick={() => setFilter("archived")} className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em] font-black transition-colors inline-flex items-center gap-1.5 ${filter === "archived" ? "bg-amber-400 text-[#0A0A0A]" : "border border-white/15 text-white/55 hover:bg-white/[0.06]"}`} data-testid="admin-enquiries-filter-archived">
     <Archive className="w-3 h-3" /> Archived
    </button>
   </div>

   {/* Bulk action bar (only when items selected) */}
   {someSelected && (
    <div className="mb-4 flex items-center gap-2 flex-wrap rounded-2xl border border-amber-300/30 bg-amber-300/[0.06] px-4 py-3" data-testid="admin-enquiries-bulkbar">
     <span className="text-amber-200 text-xs font-bold uppercase tracking-[0.2em]">{selected.size} selected</span>
     <div className="flex-1" />
     <button onClick={exportSelected} disabled={busy} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-white/85 text-[10px] uppercase tracking-[0.18em] font-bold disabled:opacity-40" data-testid="admin-enquiries-bulk-export"><Download className="w-3 h-3" /> Export</button>
     {viewingArchived ? (
      <button onClick={() => bulkArchive(false)} disabled={busy} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-400/15 hover:bg-emerald-400/25 text-emerald-200 text-[10px] uppercase tracking-[0.18em] font-bold disabled:opacity-40" data-testid="admin-enquiries-bulk-restore"><ArchiveRestore className="w-3 h-3" /> Restore</button>
     ) : (
      <button onClick={() => bulkArchive(true)} disabled={busy} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-400/15 hover:bg-sky-400/25 text-sky-200 text-[10px] uppercase tracking-[0.18em] font-bold disabled:opacity-40" data-testid="admin-enquiries-bulk-archive"><Archive className="w-3 h-3" /> Archive</button>
     )}
     <button onClick={bulkDelete} disabled={busy} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-400/15 hover:bg-rose-400/30 text-rose-200 text-[10px] uppercase tracking-[0.18em] font-bold disabled:opacity-40" data-testid="admin-enquiries-bulk-delete"><Trash2 className="w-3 h-3" /> Delete</button>
     <button onClick={() => setSelected(new Set())} className="text-white/55 hover:text-white text-[10px] uppercase tracking-[0.18em] font-bold ml-1" data-testid="admin-enquiries-bulk-clear">Clear</button>
    </div>
   )}

   {loading ? (
    <div className="text-white/45 text-sm py-12 text-center">Loading…</div>
   ) : filtered.length === 0 ? (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] py-16 text-center">
     <div className="text-white/40 text-sm">
      {items.length === 0
       ? (viewingArchived ? "No archived enquiries." : "No enquiries yet.")
       : "No enquiries match this filter."}
     </div>
    </div>
   ) : (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
     {/* Select-all header */}
     <div className="px-5 py-2.5 border-b border-white/[0.06] flex items-center gap-3 bg-white/[0.02]">
      <label className="inline-flex items-center gap-2 cursor-pointer">
       <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} className="w-4 h-4 rounded accent-amber-400 cursor-pointer" data-testid="admin-enquiries-selectall" />
       <span className="text-white/55 text-[10px] uppercase tracking-[0.22em] font-bold">{allVisibleSelected ? "Deselect all" : "Select all visible"}</span>
      </label>
     </div>

     {filtered.map((it, idx) => {
      const id = it.id || `${it.email}-${idx}`;
      const isOpen = open === id;
      const isSelected = it.id && selected.has(it.id);
      const created = it.created_at ? new Date(it.created_at).toLocaleString("en-AU") : "";
      return (
       <div
        key={id}
        className={`px-5 py-4 border-b border-white/[0.06] last:border-b-0 hover:bg-white/[0.02] transition-colors ${isOpen ? "bg-white/[0.03]" : ""} ${isSelected ? "bg-amber-300/[0.04]" : ""}`}
        data-testid={`admin-enquiry-row-${idx}`}
       >
        <div className="flex items-start gap-3">
         <input
          type="checkbox"
          checked={isSelected}
          onChange={() => it.id && toggleSelect(it.id)}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 mt-1 rounded accent-amber-400 cursor-pointer"
          data-testid={`admin-enquiry-row-${idx}-checkbox`}
         />
         <div className="flex-1 min-w-0">
          <button
           onClick={() => setOpen(isOpen ? null : id)}
           className="w-full text-left"
           data-testid={`admin-enquiry-row-${idx}-toggle`}
          >
           <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex flex-wrap items-center gap-3">
             <span className="text-white font-bold text-sm">{it.name || "(no name)"}</span>
             {it.event_type && <span className="px-2 py-0.5 rounded-full bg-white/[0.06] text-amber-200 text-[10px] uppercase tracking-wider font-black">{it.event_type}</span>}
             {it.event_date && <span className="inline-flex items-center gap-1 text-white/55 text-xs"><Calendar className="w-3 h-3" /> {it.event_date}</span>}
             {it.guests && <span className="text-white/55 text-xs">&middot; {it.guests} guests</span>}
             {it.archived && <span className="px-2 py-0.5 rounded-full bg-white/[0.06] text-white/55 text-[10px] uppercase tracking-wider font-black">Archived</span>}
            </div>
            <span className="text-white/40 text-[10px] uppercase tracking-wider">{created}</span>
           </div>
          </button>
          {/* Status pill + contact + actions */}
          <div className="mt-2 flex flex-wrap items-center gap-3">
           <StatusPill enquiry={it} onChanged={(next) => updateStatus(it, next)} testidBase={`admin-enquiry-${idx}-status`} />
           {it.email && <a onClick={(e) => e.stopPropagation()} href={`mailto:${it.email}`} className="inline-flex items-center gap-1.5 text-amber-300 hover:text-amber-200 text-xs"><Mail className="w-3 h-3" /> {it.email}</a>}
           {it.phone && <a onClick={(e) => e.stopPropagation()} href={`tel:${it.phone}`} className="inline-flex items-center gap-1.5 text-white/65 hover:text-white text-xs"><Phone className="w-3 h-3" /> {it.phone}</a>}
           <div className="flex-1" />
           {it.archived ? (
            <button onClick={() => archiveOne(it, false)} disabled={busy} className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 text-[10px] uppercase tracking-[0.18em] font-bold disabled:opacity-40" data-testid={`admin-enquiry-${idx}-restore`}>
             <ArchiveRestore className="w-3 h-3" /> Restore
            </button>
           ) : (
            <button onClick={() => archiveOne(it, true)} disabled={busy} className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200 text-[10px] uppercase tracking-[0.18em] font-bold disabled:opacity-40" data-testid={`admin-enquiry-${idx}-archive`}>
             <Archive className="w-3 h-3" /> Archive
            </button>
           )}
           <button onClick={() => deleteOne(it)} disabled={busy} className="inline-flex items-center gap-1 text-rose-300 hover:text-rose-200 text-[10px] uppercase tracking-[0.18em] font-bold disabled:opacity-40" data-testid={`admin-enquiry-${idx}-delete`}>
            <Trash2 className="w-3 h-3" /> Delete
           </button>
          </div>
          {isOpen && (
           <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 pt-3 border-t border-white/[0.06]">
            <div className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap">
             {it.message || <span className="text-white/40 italic">(no message)</span>}
             {it.source_page ? <div className="mt-3 text-[10px] text-white/40 uppercase tracking-wider">From: {it.source_page}</div> : null}
            </div>
           </div>
          )}
         </div>
        </div>
       </div>
      );
     })}
    </div>
   )}
  </div>
 );
}
